import { z } from "zod";
import { TObjectBase } from "../base";
import { TCRUDBy } from "../common/CRUDBy";
import { TReadAccess } from "../common/readAccess";

export type IDashboardLocation = z.infer<typeof TDashboardLocation>;
export const TDashboardLocation = z.enum([
  "dashboard",
  "properties",
  "property",
  "insights",
]);

export type IDashboardPlatform = z.infer<typeof TDashboardPlatform>;
export const TDashboardPlatform = z.enum(["web", "phone"]);

export type IDashboardWidgetAction = z.infer<
  typeof TDashboardWidgetActionsOptions
>;
export const TDashboardWidgetActionsOptions = z.object({
  actionID: z.string().nullish(),
  actionTitle: z.optional(
    z.record(
      z.object({
        default: z.string(),
      })
    )
  ),
  phone: z.string().nullish(),
  externalURL: z.string().nullish(),
});

export type IDashboardWidgetOptions = z.infer<typeof TDashboardWidgetOptions>;
export const TDashboardWidgetOptions = z.object({
  title: z.optional(
    z.record(
      z.object({
        default: z.string(),
      })
    )
  ),

  collectionID: z.optional(z.string()),

  roles: z.optional(z.array(z.number())),

  excludeIDs: z.optional(z.array(z.string())),

  welcomeText: z.optional(
    z.object({
      title: z.optional(
        z
          .record(
            z.object({
              default: z.string(),
            })
          )
          .or(z.string())
      ),
      content: z.optional(
        z
          .record(
            z.object({
              default: z.string(),
            })
          )
          .or(z.string())
      ),
    })
  ),

  actions: z.optional(z.record(TDashboardWidgetActionsOptions)),
  actionsOrder: z.optional(z.array(z.string())),
  reportIDs: z.optional(z.array(z.string())),

  filters: z
    .record(
      z.object({
        comparator: z.string(),
        value: z.any().nullish(),
      })
    )
    .optional(),
});

export type IDashboardWidget = z.infer<typeof TDashboardWidget>;
export const TDashboardWidget = z.object({
  widgetID: z.string(),
  options: z.optional(TDashboardWidgetOptions),
});

export type IDashboard = z.infer<typeof TDashboard>;
export const TDashboard = z
  .object({
    name: z.string(),

    allowCompanyMembersToEdit: z.optional(z.boolean()),

    locations: z.array(TDashboardLocation),
    platforms: z.array(TDashboardPlatform),
    layout: z.optional(z.string()),
    isVisibleForPropertyMembers: z.optional(z.boolean()),

    shareWithProperties: z.optional(z.array(z.string())),
    shareWithRoles: z.optional(z.array(z.number())),

    widgets: z.record(TDashboardWidget),
    widgetsGrid: z.optional(
      z.array(
        z.object({
          i: z.string(),
          x: z.number(),
          y: z.number(),
          w: z.number(),
          h: z.number(),
        })
      )
    ),

    widgetsOrder: z.array(z.string()).nullish(),
    isTemplate: z.boolean().nullish(),

    readAccess: TReadAccess,
    writeAccess: z.optional(TReadAccess),

    updatedBy: z.optional(TCRUDBy),
  })
  .extend(TObjectBase.shape);

export type IDashboards = z.infer<typeof TDashboards>;
export const TDashboards = z.array(TDashboard);
